import { IntelligenceFixture } from '../../types';
import { PLACEHOLDER_CONFIDENCE_REASON } from '../placeholderCopy';
import { FIXTURE_UPDATED_AT } from '../updatedAt';

/**
 * Read off the LIVE Figma canvas for band `589-29715` at 2.6x-4x, where the small text resolves.
 * Everything below is what that band prints. The three cards other than `ready_for_engagement` used
 * to carry INVENTED figures (`plan.md § AF-1`, "no populated variant had been read") — the file does
 * draw a populated variant of all four, and none of the invented numbers were in it.
 *
 * `589-29715` and `589:32434` are two near-identical 1360x1469 frames both named `Group 40456`, at
 * Left 299 and Top 9465.8 / 9547.8. They DISAGREE on `ready_for_engagement`'s first two tiles — see
 * the note on that card. This file follows `589-29715`.
 */

// FOUR tiles carry a SEVEN-bar trend — both of `growth_opportunities_product_interest` and both of
// `growth_planner_handoff_readiness`. Measured off `589:29717` at 100% where one image pixel is one
// Figma pixel: bar heights 18 / 11 / 24 / 37 / 22 / 9 / 6 px on a shared baseline, 8px bars at an
// 11.5px pitch, the fourth — the peak — in green. `589-29936` draws the handoff pair with the SAME
// profile, re-measured independently at 100%: bar runs 18 / 11 / 24 / 37 / 22 / 9 / 5 with the
// fourth again the green one.
// The file prints no figures against those bars, so only the SHAPE is parity; each series below is
// that shape scaled so its peak is the tile's own reading, which is the one figure the card states.
// That is why a series can be added to the handoff tiles without inventing a value: it restates the
// figure already printed on the tile through the shape already measured off the screens.
const TILE_TREND_PROFILE = [18, 11, 24, 37, 22, 9, 6];

const trendFor = (peak: number): number[] => {
  const tallest = Math.max(...TILE_TREND_PROFILE);

  return TILE_TREND_PROFILE.map(height => Math.round((height / tallest) * peak));
};

// Every NRS component raises a score a rise in is good, per its governed formula. PLA-8815.
const DRIVES = 'positive' as const;

export const growthOpportunitiesCardFixtures: Record<string, IntelligenceFixture> = {
  growth_opportunities_product_interest: {
    cardKey: 'growth_opportunities_product_interest',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    breakdownKey: 'opportunity_signal',
    // TWO tiles. `Eligible signals` 8,420 and `Unique customers` 5,310 were invented; the card draws
    // these labels and these figures, both carrying the band's recurring `↑ 1,530 (9.4% pp)`.
    metrics: [
      {
        key: 'opportunity_signal_count',
        label: 'Opportunity Signal Count',
        value: 18420,
        format: 'count',
        delta: {
          value: 1530,
          unit: 'count',
          direction: 'up',
          sentiment: 'positive',
          secondary: { value: 9.4, unit: 'pp' }
        },
        sparkline: trendFor(18420)
      },
      {
        key: 'customers_with_active_signals',
        label: 'Customers with Active Signals',
        value: 31760,
        format: 'count',
        delta: {
          value: 1530,
          unit: 'count',
          direction: 'up',
          sentiment: 'positive',
          secondary: { value: 9.4, unit: 'pp' }
        },
        sparkline: trendFor(31760)
      }
    ],
    confidence: {
      score: 82,
      band: 'high',
      reasons: [PLACEHOLDER_CONFIDENCE_REASON]
    },
    whyThisResult:
      'Opportunity volume rose because improved transaction recency and product-usage signals increased readiness. Eligibility, consent and responsible-selling guardrails are applied before a product is shown.'
  },

  contact_restrictions: {
    cardKey: 'contact_restrictions',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    // THREE tiles, not two. `Customers under a contact restriction` 15,240 and `Restriction rate`
    // 8.3% were both invented. The three the card draws reconcile exactly: 9,720 / 64,800 = 15.0%.
    metrics: [
      {
        key: 'suppression_rate',
        label: 'Suppression Rate',
        value: 15.0,
        format: 'percent',
        // The card contradicts ITSELF on this movement. The tile glyph is a red `↓ 2.2 pp`, while
        // the ring beside it reads `Previous: 12.8%` and `Change: +2.2 percentage points` and the
        // why-box opens `Suppression increased`. 12.8 + 2.2 = the 15.0 printed here, so three
        // readings say up and only the glyph says down. `up` is taken and the conflict reported
        // rather than closed; the red ink is what both readings agree on, and more customers going
        // out of reach is the wrong direction whichever way the arrow points.
        delta: { value: 2.2, unit: 'pp', direction: 'up', sentiment: 'negative' }
      },
      {
        key: 'suppressed_customers',
        label: 'Suppressed Customers / Signals',
        value: 9720,
        format: 'count',
        // Painted GREEN in the file. `MetricDelta.sentiment` is business meaning, not direction
        // (FR § Period comparison display: "a rise in not-eligible customers takes negative
        // treatment"), and a rise in suppressed customers is that case, so the rule wins over the ink.
        delta: {
          value: 1530,
          unit: 'count',
          direction: 'up',
          sentiment: 'negative',
          secondary: { value: 9.4, unit: 'pp' }
        }
      },
      {
        key: 'eligible_customers',
        label: 'Eligible Customer',
        value: 64800,
        format: 'count',
        delta: {
          value: 1530,
          unit: 'count',
          direction: 'up',
          sentiment: 'positive',
          secondary: { value: 9.4, unit: 'pp' }
        }
      }
    ],
    confidence: {
      score: 82,
      band: 'high',
      // The two authored sentences that stood here were invented — the file draws the short Lorem.
      reasons: [PLACEHOLDER_CONFIDENCE_REASON]
    },
    whyThisResult:
      'Suppression increased mainly because 1,140 additional customers withdrew or lacked purpose-specific consent and the contact-fatigue threshold blocked 620 more customers after recent campaign activity.'
  },

  ready_for_engagement: {
    cardKey: 'ready_for_engagement',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    breakdownKey: 'ready_customers',
    /**
     * THE TWO FRAMES DISAGREE HERE, and the disagreement is left standing rather than resolved.
     *
     * `589:32774` (frame `589:32434`, the node this card was rebuilt on) draws `78 High` and
     * `22,640 ↑ 7.2% pp`. `589-29715` draws `78 ↑ 7 pts` and `22,640 ↑ 1,100 (7.2% pp)` — a
     * movement where the other frame prints a band word, and the band's recurring absolute-plus-
     * percent form where the other prints the percent alone. Tile 3 agrees: `84% High`.
     *
     * Both were read off the live canvas at a zoom that resolves the glyphs, so neither is a
     * misreading. The tiles stay on `589:32774` until design says which frame is current.
     */
    metrics: [
      {
        key: 'nudge_readiness_score',
        label: 'Nudge Readiness Score',
        // O-COM-026's own component means, UAT `generic` 2026-09-08, strongest first. Six of the
        // formula's seven terms: `consent_allowed` has no column and is absent rather than drawn at
        // zero. Every one raises a score a rise in is good, so none is a blocker. PLA-8815.
        factors: [
          { key: 'nrs_mean_component_recency', label: 'Recency', value: 0.7411, tone: DRIVES },
          {
            key: 'nrs_mean_component_confidence',
            label: 'Confidence',
            value: 0.628868,
            tone: DRIVES
          },
          {
            key: 'nrs_mean_component_propensity',
            label: 'Propensity',
            value: 0.496842,
            tone: DRIVES
          },
          {
            key: 'nrs_mean_component_reachability',
            label: 'Channel reachability',
            value: 0.445105,
            tone: DRIVES
          },
          { key: 'nrs_mean_component_no_fatigue', label: 'No fatigue', value: 0, tone: DRIVES },
          { key: 'nrs_mean_component_no_risk', label: 'No high risk', value: 0, tone: DRIVES }
        ],
        value: 78,
        format: 'points',
        status: { label: 'High', tone: 'positive' }
      },
      {
        key: 'ready_customers',
        label: 'Ready Customers',
        value: 22640,
        format: 'count',
        delta: { value: 7.2, unit: 'pp', direction: 'up', sentiment: 'positive' }
      },
      {
        key: 'result_confidence',
        label: 'Result Confidence',
        value: 84,
        format: 'percent',
        status: { label: 'High', tone: 'positive' }
      }
    ],
    // No `evidence` panel: neither frame draws a rows-and-shares block on this card.
    evidenceNote:
      'weighted propensity, confidence, recency, channel reachability, consent allowed, no fatigue and no high risk.',
    confidence: {
      score: 82,
      band: 'high',
      reasons: [PLACEHOLDER_CONFIDENCE_REASON]
    },
    whyThisResult:
      'Readiness increased because propensity, recent activity and channel reachability improved. Consent restriction, contact fatigue or high risk remains a hard blocker and forces the affected customer into Not Ready regardless of the weighted score.'
  },

  growth_planner_handoff_readiness: {
    cardKey: 'growth_planner_handoff_readiness',
    state: { kind: 'ok' },
    updatedAt: FIXTURE_UPDATED_AT,
    // TWO tiles counting SIGNALS, not the customers the invented pair reported. `Customers eligible
    // for handoff` 48,120 and `Handoff ready rate` 50.1% are both gone: the card's own gate rail
    // opens on 18,420 — card 1's opportunity signal count — and 8,940 + 9,480 returns exactly that.
    metrics: [
      {
        key: 'handoff_eligible_signals',
        label: 'Growth Planner Handoff Eligible',
        value: 8940,
        format: 'count',
        delta: {
          value: 3250,
          unit: 'count',
          direction: 'up',
          sentiment: 'positive',
          secondary: { value: 48.5, unit: 'pp' }
        },
        sparkline: trendFor(8940)
      },
      {
        key: 'handoff_blocked_signals',
        label: 'Not Eligible / Blocked',
        value: 9480,
        format: 'count',
        delta: {
          value: 1530,
          unit: 'count',
          direction: 'up',
          sentiment: 'negative',
          secondary: { value: 9.4, unit: 'pp' }
        },
        sparkline: trendFor(9480)
      }
    ],
    confidence: {
      score: 82,
      band: 'high',
      reasons: [PLACEHOLDER_CONFIDENCE_REASON]
    }
    // No `whyThisResult`: the file draws no such box on this card. The space the other three give it
    // is taken by the two callout panels and the handoff strip. Read over the whole card at 2.6x and
    // 3.2x — the one card of the four with nothing between its gate rail and its `Updated` stamp.
    // The sentence that stood here ("The gates run in order and a customer leaves at the first one
    // it fails…") was invented, and its subject — customers — is not what this card counts.
  }
};
